import { ButtonHTMLAttributes, memo, forwardRef, ReactNode } from 'react';
import './Button.css';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Visual style variant.
   * - `primary`   – teal glow, brand CTA
   * - `secondary` – default glass (spec: #FFFFFF01)
   * - `ghost`     – text-only, no border/bg
   * - `danger`    – red tint for destructive actions
   * - `warning`   – amber/yellow tint for warnings
   */
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'warning';
  /** Button size */
  size?: 'sm' | 'md' | 'lg';
  /** Stretch to container width */
  fullWidth?: boolean;
  /** Shows spinner; disables interaction */
  isLoading?: boolean;
  /** Icon to the left of the label */
  leftIcon?: ReactNode;
  /** Icon to the right of the label */
  rightIcon?: ReactNode;
}

/**
 * AuthButton - Dark-theme button atom.
 *
 * Spec:
 *  - background: #FFFFFF01 (glass, near-transparent)
 *  - border-radius: 22px
 *  - height: 52px
 *  - width: 448px (but uses 100% for responsiveness)
 *
 * Usage:
 *  <Button variant="primary" fullWidth isLoading={isPending}>Sign In</Button>
 *  <Button variant="ghost" leftIcon={<ArrowLeft size={14}/>}>Back</Button>
 */
export const Button = memo(
  forwardRef<HTMLButtonElement, ButtonProps>(
    (
      {
        children,
        variant = 'secondary',
        size = 'md',
        fullWidth = false,
        isLoading = false,
        leftIcon,
        rightIcon,
        disabled,
        className = '',
        ...props
      },
      ref
    ) => {
      const classes = [
        'auth-btn',
        `auth-btn--${variant}`,
        `auth-btn--${size}`,
        fullWidth   && 'auth-btn--full',
        isLoading   && 'auth-btn--loading',
        className,
      ]
        .filter(Boolean)
        .join(' ');

      return (
        <button
          ref={ref}
          className={classes}
          disabled={disabled || isLoading}
          aria-busy={isLoading}
          {...props}
        >
          {isLoading ? (
            <>
              <span className="auth-btn__spinner" aria-hidden="true" />
              <span style={{ opacity: 0.6, fontSize: '13px' }}>Please wait…</span>
            </>
          ) : (
            <>
              {leftIcon  && <span className="auth-btn__icon">{leftIcon}</span>}
              <span className={isLoading ? 'auth-btn__text--hidden' : ''}>{children}</span>
              {rightIcon && <span className="auth-btn__icon">{rightIcon}</span>}
            </>
          )}
        </button>
      );
    }
  )
);

Button.displayName = 'Button';
